import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

// Helper component for text inputs with auto-save
const TextQuestion = ({ id, question, value, onChange, multiline = false, maxLength, placeholder }) => {
  const [localValue, setLocalValue] = useState(value || "");

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleBlur = () => {
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const Component = multiline ? Textarea : Input;

  return (
    <div className="question-card bg-white rounded-lg border p-6 mb-6" data-testid={`question-${id}`}>
      <label className="block text-base font-medium text-slate-800 mb-3">{question}</label>
      <Component
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder || "Enter your response..."}
        maxLength={maxLength}
        rows={multiline ? 4 : undefined}
        className="w-full"
        data-testid={`input-${id}`}
      />
      {maxLength && (
        <p className="text-xs text-muted-foreground mt-2 text-right">
          {localValue.length}/{maxLength} characters
        </p>
      )}
    </div>
  );
};

// Helper component for single choice questions with "Other" option
const SingleChoiceQuestion = ({ id, question, options, value, onChange, hasOther = true }) => {
  const [otherText, setOtherText] = useState("");
  const isOtherSelected = value && value.startsWith("Other:");
  
  useEffect(() => {
    if (isOtherSelected) {
      setOtherText(value.replace("Other: ", ""));
    }
  }, [value, isOtherSelected]);

  const handleOptionChange = (option) => {
    if (option === "Other") {
      onChange(`Other: ${otherText}`);
    } else {
      onChange(option);
    }
  };

  const handleOtherTextChange = (text) => {
    setOtherText(text);
    onChange(`Other: ${text}`);
  };

  return (
    <div className="question-card bg-white rounded-lg border p-6 mb-6" data-testid={`question-${id}`}>
      <p className="text-base font-medium text-slate-800 mb-4">{question}</p>
      <RadioGroup 
        value={isOtherSelected ? "Other" : (value || "")} 
        onValueChange={handleOptionChange}
        className="space-y-3"
      >
        {options.map((option, index) => (
          <div
            key={index}
            className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
              value === option ? 'border-[#003366] bg-blue-50' : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => handleOptionChange(option)}
          >
            <RadioGroupItem
              value={option}
              id={`${id}-${index}`}
              className="border-slate-300 text-[#003366]"
            />
            <Label htmlFor={`${id}-${index}`} className="cursor-pointer flex-1 text-sm">
              {option}
            </Label>
          </div>
        ))}
        {hasOther && (
          <div className={`flex flex-col gap-2 p-3 rounded-lg border transition-colors ${
            isOtherSelected ? 'border-[#003366] bg-blue-50' : 'border-slate-200'
          }`}>
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleOptionChange("Other")}>
              <RadioGroupItem value="Other" id={`${id}-other`} className="border-slate-300 text-[#003366]" />
              <Label htmlFor={`${id}-other`} className="cursor-pointer text-sm">Other (please specify)</Label>
            </div>
            {isOtherSelected && (
              <Input
                value={otherText}
                onChange={(e) => handleOtherTextChange(e.target.value)}
                placeholder="Please specify..."
                className="ml-7 mt-2"
                autoFocus
              />
            )}
          </div>
        )}
      </RadioGroup>
    </div>
  );
};

// Skill rating table
const SkillRatingTable = ({ id, question, skills, value, onChange }) => {
  const [localValue, setLocalValue] = useState(value || {});

  useEffect(() => {
    setLocalValue(value || {});
  }, [value]);

  const handleRating = (skill, rating) => {
    const newValue = { ...localValue, [skill]: rating };
    setLocalValue(newValue);
    onChange(newValue);
  };

  return (
    <div className="question-card bg-white rounded-lg border p-6 mb-6" data-testid={`question-${id}`}>
      <p className="text-base font-medium text-slate-800 mb-4">{question}</p>
      <div className="text-xs text-muted-foreground mb-4 flex flex-wrap justify-end gap-2">
        <span className="bg-slate-100 px-2 py-1 rounded">1 = No experience</span>
        <span className="bg-slate-100 px-2 py-1 rounded">3 = Working knowledge</span>
        <span className="bg-slate-100 px-2 py-1 rounded">5 = Expert</span>
      </div>
      <div className="space-y-3">
        {skills.map((skill, idx) => (
          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <span className="text-sm font-medium flex-1">{skill}</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => handleRating(skill, rating)}
                  className={`w-9 h-9 rounded text-sm font-medium transition-all ${
                    localValue[skill] === rating 
                      ? 'bg-[#003366] text-white' 
                      : 'bg-white border border-slate-200 hover:border-[#003366]'
                  }`}
                >
                  {rating}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Agreement scale question
const AgreementQuestion = ({ id, question, value, onChange }) => {
  const options = ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"];

  return (
    <div className="question-card bg-white rounded-lg border p-6 mb-6" data-testid={`question-${id}`}>
      <p className="text-base font-medium text-slate-800 mb-4">{question}</p>
      <div className="flex gap-2 flex-wrap">
        {options.map((option, index) => (
          <button
            key={index}
            type="button"
            onClick={() => onChange(option)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
              value === option
                ? 'bg-[#003366] border-[#003366] text-white'
                : 'border-slate-200 hover:border-[#003366] text-slate-600'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

// Multiple choice with checkboxes and "Other" option
const MultiChoiceQuestion = ({ id, question, options, value, onChange, hasOther = true }) => {
  const [otherText, setOtherText] = useState("");
  const currentValues = Array.isArray(value) ? value : [];
  const otherValue = currentValues.find(v => v && v.startsWith("Other:"));
  
  useEffect(() => {
    if (otherValue) {
      setOtherText(otherValue.replace("Other: ", ""));
    }
  }, [otherValue]);

  const handleToggle = (option) => {
    if (currentValues.includes(option)) {
      onChange(currentValues.filter(v => v !== option));
    } else {
      onChange([...currentValues, option]);
    }
  };

  const handleOtherToggle = () => {
    if (otherValue) {
      onChange(currentValues.filter(v => !v.startsWith("Other:")));
    } else {
      onChange([...currentValues, `Other: ${otherText}`]);
    }
  };

  const handleOtherTextChange = (text) => {
    setOtherText(text);
    const filtered = currentValues.filter(v => !v.startsWith("Other:"));
    onChange([...filtered, `Other: ${text}`]);
  };

  return (
    <div className="question-card bg-white rounded-lg border p-6 mb-6" data-testid={`question-${id}`}>
      <p className="text-base font-medium text-slate-800 mb-2">{question}</p>
      <p className="text-xs text-muted-foreground mb-4">Select all that apply</p>
      <div className="space-y-2">
        {options.map((option, index) => {
          const isSelected = currentValues.includes(option);
          return (
            <div
              key={index}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                isSelected ? 'border-[#003366] bg-blue-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => handleToggle(option)}
            >
              <Checkbox checked={isSelected} />
              <Label className="cursor-pointer flex-1 text-sm">{option}</Label>
            </div>
          );
        })}
        {hasOther && (
          <div className={`flex flex-col gap-2 p-3 rounded-lg border transition-colors ${
            otherValue ? 'border-[#003366] bg-blue-50' : 'border-slate-200'
          }`}>
            <div className="flex items-center gap-3 cursor-pointer" onClick={handleOtherToggle}>
              <Checkbox checked={!!otherValue} />
              <Label className="cursor-pointer text-sm">Other (please specify)</Label>
            </div>
            {otherValue && (
              <Input
                value={otherText}
                onChange={(e) => handleOtherTextChange(e.target.value)}
                placeholder="Please specify..."
                className="ml-7 mt-2"
                autoFocus
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const CurrentStateSection = ({ responses, onSave, profile }) => {
  const getValue = (questionId) => responses?.[questionId];
  const managerName = profile?.manager_name || "[Manager Name]";

  return (
    <div className="space-y-2">
      {/* Section intro */}
      <Card className="bg-[#f0f7ff] border-[#003366]/20 mb-8">
        <CardContent className="pt-6">
          <p className="text-sm text-[#003366]">
            This section focuses on your current skills, tools, and daily responsibilities at Sagicor.
            Please rate your proficiency honestly – this assessment is about understanding, not judging.
          </p>
        </CardContent>
      </Card>

      {/* 2.1 Technology Environment */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          2.1 Technology Environment
        </h3>

        <TextQuestion
          id="2_1_q13"
          question="13. List the primary systems, applications, or technologies you work with daily:"
          value={getValue("2_1_q13")}
          onChange={(val) => onSave("2_1_q13", val)}
          multiline
          maxLength={1500}
          placeholder="e.g., Windows Server, ServiceNow, SQL Server, Azure, AWS..."
        />

        <TextQuestion
          id="2_1_q14"
          question="14. Describe a typical day in your role – what tasks consume most of your time?"
          value={getValue("2_1_q14")}
          onChange={(val) => onSave("2_1_q14", val)}
          multiline
          maxLength={2000}
        />
      </div>

      {/* 2.2 Technical Skills */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          2.2-2.4 Technical Skills Assessment
        </h3>

        <SkillRatingTable
          id="2_2_q15"
          question="15. Rate your proficiency in these infrastructure skills:"
          skills={[
            "Windows Server Administration",
            "Linux/Unix Administration",
            "Active Directory / Identity Management",
            "Virtualization (VMware, Hyper-V)",
            "Cloud Platforms (AWS, Azure, GCP)",
            "Network Administration",
            "Storage & Backup Systems",
            "Scripting (PowerShell, Bash, Python)"
          ]}
          value={getValue("2_2_q15")}
          onChange={(val) => onSave("2_2_q15", val)}
        />

        <SkillRatingTable
          id="2_2_q16"
          question="16. Rate your proficiency in these service management skills:"
          skills={[
            "Incident Management",
            "Problem Management", 
            "Change Management",
            "Service Request Fulfillment",
            "Knowledge Management",
            "ITIL Processes",
            "ServiceNow / ITSM Tools"
          ]}
          value={getValue("2_2_q16")}
          onChange={(val) => onSave("2_2_q16", val)}
        />
      </div>

      {/* 2.5 How You Approach Work */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          2.5 How You Approach Work
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          The following questions present workplace scenarios. Choose the response that best describes how you would typically handle each situation.
        </p>

        <SingleChoiceQuestion
          id="2_5_q17"
          question="17. You discover a security vulnerability that could delay a major release. You would:"
          options={[
            "Report it immediately to my manager and security team, even if it delays the release",
            "Try to fix it quietly myself before anyone notices",
            "Document it but wait until after the release to address it",
            "Mention it casually to see if anyone else thinks it's a problem"
          ]}
          value={getValue("2_5_q17")}
          onChange={(val) => onSave("2_5_q17", val)}
        />

        <SingleChoiceQuestion
          id="2_5_q18"
          question="18. You accidentally caused a system outage, but no one knows it was you. You would:"
          options={[
            "Immediately own up to it and focus on fixing the issue",
            "Fix it quietly and hope no one investigates",
            "Wait to see if anyone asks before admitting involvement",
            "Focus on the fix and decide later whether to mention my role"
          ]}
          value={getValue("2_5_q18")}
          onChange={(val) => onSave("2_5_q18", val)}
        />

        <SingleChoiceQuestion
          id="2_5_q19"
          question="19. A colleague urgently needs help with their project, but you're behind on your own work. You would:"
          options={[
            "Help them immediately – team success matters more than my individual work",
            "Explain my situation and offer to help after I catch up",
            "Point them to resources but continue with my own work",
            "Decline – my deadlines are more important"
          ]}
          value={getValue("2_5_q19")}
          onChange={(val) => onSave("2_5_q19", val)}
        />

        <SingleChoiceQuestion
          id="2_5_q20"
          question="20. You notice an inefficient process that everyone accepts. You would:"
          options={[
            "Document the inefficiency and propose improvements to leadership",
            "Mention it to colleagues to gauge interest in changing it",
            "Work around it myself but not rock the boat",
            "Accept it – there's probably a good reason it's done this way"
          ]}
          value={getValue("2_5_q20")}
          onChange={(val) => onSave("2_5_q20", val)}
        />

        <SingleChoiceQuestion
          id="2_5_q21"
          question="21. A junior team member suggests a solution you disagree with in a meeting. You would:"
          options={[
            "Listen openly, ask clarifying questions, and consider their perspective",
            "Politely explain why I think my approach is better",
            "Let the team decide without pushing my opinion",
            "Dismiss the idea – experience matters"
          ]}
          value={getValue("2_5_q21")}
          onChange={(val) => onSave("2_5_q21", val)}
        />
      </div>

      {/* 2.6 Working with Manager */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          2.6 Working with Your Manager
        </h3>

        <SingleChoiceQuestion
          id="2_6_q22"
          question={`22. How often do you have 1:1 meetings with ${managerName}?`}
          options={["Weekly", "Bi-weekly", "Monthly", "Quarterly", "Rarely/Never"]}
          value={getValue("2_6_q22")}
          onChange={(val) => onSave("2_6_q22", val)}
        />

        <AgreementQuestion
          id="2_6_q23"
          question={`23. ${managerName} provides clear direction on priorities and expectations.`}
          value={getValue("2_6_q23")}
          onChange={(val) => onSave("2_6_q23", val)}
        />

        <AgreementQuestion
          id="2_6_q24"
          question={`24. I receive timely and constructive feedback from ${managerName}.`}
          value={getValue("2_6_q24")}
          onChange={(val) => onSave("2_6_q24", val)}
        />

        <AgreementQuestion
          id="2_6_q25"
          question={`25. ${managerName} supports my career development and growth.`}
          value={getValue("2_6_q25")}
          onChange={(val) => onSave("2_6_q25", val)}
        />

        <AgreementQuestion
          id="2_6_q26"
          question={`26. I feel comfortable raising concerns or issues with ${managerName}.`}
          value={getValue("2_6_q26")}
          onChange={(val) => onSave("2_6_q26", val)}
        />

        <TextQuestion
          id="2_6_q27"
          question={`27. What works well in your working relationship with ${managerName}?`}
          value={getValue("2_6_q27")}
          onChange={(val) => onSave("2_6_q27", val)}
          multiline
          maxLength={1000}
        />

        <TextQuestion
          id="2_6_q28"
          question={`28. What could be improved in your working relationship with ${managerName}?`}
          value={getValue("2_6_q28")}
          onChange={(val) => onSave("2_6_q28", val)}
          multiline
          maxLength={1000}
        />
      </div>

      {/* 2.7 Organizational Experience */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          2.7 Organizational Experience
        </h3>

        <AgreementQuestion
          id="2_7_q29"
          question="29. I know what is expected of me at work."
          value={getValue("2_7_q29")}
          onChange={(val) => onSave("2_7_q29", val)}
        />

        <AgreementQuestion
          id="2_7_q30"
          question="30. I have the materials and equipment I need to do my work right."
          value={getValue("2_7_q30")}
          onChange={(val) => onSave("2_7_q30", val)}
        />

        <AgreementQuestion
          id="2_7_q31"
          question="31. At work, I have the opportunity to do what I do best every day."
          value={getValue("2_7_q31")}
          onChange={(val) => onSave("2_7_q31", val)}
        />

        <AgreementQuestion
          id="2_7_q32"
          question="32. In the last seven days, I have received recognition for doing good work."
          value={getValue("2_7_q32")}
          onChange={(val) => onSave("2_7_q32", val)}
        />

        <AgreementQuestion
          id="2_7_q33"
          question="33. My opinions seem to count at work."
          value={getValue("2_7_q33")}
          onChange={(val) => onSave("2_7_q33", val)}
        />

        <AgreementQuestion
          id="2_7_q34"
          question="34. The mission/purpose of Sagicor makes me feel my job is important."
          value={getValue("2_7_q34")}
          onChange={(val) => onSave("2_7_q34", val)}
        />
      </div>

      {/* 2.8 Recognition Preferences */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          2.8 Recognition Preferences
        </h3>

        <MultiChoiceQuestion
          id="2_8_q35"
          question="35. How do you prefer to be recognized for your contributions?"
          options={[
            "Public acknowledgment in team meetings",
            "Private praise from my manager",
            "Written recognition (email, certificate)",
            "Financial rewards (bonus, gift card)",
            "Additional time off",
            "Opportunities for interesting projects",
            "Professional development opportunities",
            "Promotion or title advancement",
            "I do not need recognition – the work is reward enough"
          ]}
          value={getValue("2_8_q35")}
          onChange={(val) => onSave("2_8_q35", val)}
          hasOther
        />

        <TextQuestion
          id="2_8_q36"
          question="36. Describe the most meaningful recognition you have received in your career (at Sagicor or elsewhere). What made it meaningful?"
          value={getValue("2_8_q36")}
          onChange={(val) => onSave("2_8_q36", val)}
          multiline
          maxLength={1500}
        />
      </div>
    </div>
  );
};

export default CurrentStateSection;
